// Insignias de estado y de origen, y estados de operación. Solo pintan lo que dice el backend: no hay
// estados inventados por la interfaz (reserva y despacho son indicadores DERIVADOS, no estados).
import { useState, type ReactNode } from 'react';
import type { ErrorPresentable } from '../errores/catalogo';
import { presentarError } from '../errores/catalogo';

const TONO_NOTA: Record<string, string> = {
  BORRADOR: 'neutro',
  EN_APROBACION: 'aviso',
  CONFIRMADA: 'ok',
  CERRADA_CORTA: 'info',
  ANULADA: 'malo',
  DESCARTADA: 'apagado',
};
const NOMBRE_NOTA: Record<string, string> = {
  BORRADOR: 'Borrador',
  EN_APROBACION: 'En aprobación',
  CONFIRMADA: 'Confirmada',
  CERRADA_CORTA: 'Cerrada corta',
  ANULADA: 'Anulada',
  DESCARTADA: 'Descartada',
};
export function EstadoNota({ estado }: { estado: string }) {
  return (
    <span className={`insignia tono-${TONO_NOTA[estado] ?? 'neutro'}`} data-estado={estado}>
      {NOMBRE_NOTA[estado] ?? estado}
    </span>
  );
}

const ORIGEN: Record<string, { texto: string; titulo: string }> = {
  LISTA: { texto: 'LISTA', titulo: 'Precio de la lista del cliente' },
  LISTA_ESCALA: { texto: 'ESCALA', titulo: 'Precio del tramo de escala (D-50)' },
  MANUAL: { texto: 'MANUAL', titulo: 'Precio manual autorizado (D-60)' },
  COTIZACION: { texto: 'COTIZ.', titulo: 'Precio comprometido por una cotización' },
};
export function OrigenPrecio({ origen }: { origen: string | null }) {
  if (!origen)
    return (
      <span className="insignia tono-malo" title="Sin precio: la nota no se confirma">
        SIN PRECIO
      </span>
    );
  const o = ORIGEN[origen] ?? { texto: origen, titulo: origen };
  return (
    <span
      className={`insignia origen-${origen.toLowerCase()}`}
      title={o.titulo}
      data-origen={origen}
    >
      {o.texto}
    </span>
  );
}

export function EstadoCredito({ resultado }: { resultado: string }) {
  const tono = resultado === 'APROBADO' ? 'ok' : resultado === 'RECHAZADO' ? 'malo' : 'aviso';
  const texto =
    resultado === 'APROBADO'
      ? 'Crédito aprobado'
      : resultado === 'RECHAZADO'
        ? 'Crédito rechazado'
        : 'Requiere autorización';
  return (
    <span className={`insignia tono-${tono}`} data-credito={resultado}>
      {texto}
    </span>
  );
}

export function Cumplimiento({ estado }: { estado: string }) {
  const m: Record<string, [string, string]> = {
    SIN_DESPACHO: ['neutro', 'Sin despacho'],
    PARCIAL: ['aviso', 'Despacho parcial'],
    COMPLETA: ['ok', 'Despachada'],
  };
  const [tono, texto] = m[estado] ?? ['neutro', estado];
  return <span className={`insignia tono-${tono}`}>{texto}</span>;
}

/** Los estados de operación NO son un spinner genérico: cada uno dice qué está pasando. */
export type Operacion =
  'cargando' | 'guardando' | 'procesando' | 'esperando_sii' | 'listo' | 'error';
const OPERACION: Record<Operacion, string> = {
  cargando: 'Cargando…',
  guardando: 'Guardando…',
  procesando: 'Procesando…',
  esperando_sii: 'Esperando al SII…',
  listo: 'Listo',
  error: 'Error',
};
export function EstadoOperacion({ op }: { op: Operacion }) {
  return (
    <span className={`operacion op-${op}`} role="status" aria-live="polite" data-operacion={op}>
      {op !== 'listo' && op !== 'error' && <span className="punto-actividad" aria-hidden />}
      {OPERACION[op]}
    </span>
  );
}

/** La instancia de simulación (copia UAT con todo habilitado) del mismo servidor, en el puerto 8443. */
export const urlSimulacion = () => `https://${window.location.hostname}:8443/`;

/** Error presentado con su código técnico y correlación (para soporte), plegable. */
export function PanelError({
  error,
  alCerrar,
  children,
}: {
  error: unknown;
  alCerrar?: () => void;
  children?: ReactNode;
}) {
  const [ver, setVer] = useState(false);
  const p: ErrorPresentable = presentarError(error);
  return (
    <div className={`panel-error sev-${p.severidad}`} role="alert" data-codigo={p.codigo}>
      <div className="panel-error-fila">
        <strong>{p.titulo}</strong>
        {alCerrar && (
          <button
            type="button"
            className="boton-icono"
            onClick={alCerrar}
            aria-label="Cerrar aviso"
          >
            ✕
          </button>
        )}
      </div>
      <p>{p.mensaje}</p>
      {/^VALIDACIÓN OPERACIONAL:/.test(p.mensaje) && (
        // Lo que preproducción no ejecuta (OD, despacho, facturación…) se recorre en la simulación.
        <p>
          <a className="enlace-simulacion" href={urlSimulacion()} target="_blank" rel="noopener">
            Hacerlo en la SIMULACIÓN (erp.madeci.cl:8443) →
          </a>
        </p>
      )}
      {children}
      <button type="button" className="enlace" onClick={() => setVer((v) => !v)}>
        {ver ? 'Ocultar detalle técnico' : 'Detalle técnico'}
      </button>
      {ver && (
        <dl className="detalle-tecnico">
          <dt>Código</dt>
          <dd>
            <code>{p.codigo}</code>
          </dd>
          {p.status > 0 && (
            <>
              <dt>HTTP</dt>
              <dd>{p.status}</dd>
            </>
          )}
          {p.correlacionId && (
            <>
              <dt>Correlación</dt>
              <dd>
                <code>{p.correlacionId}</code>
              </dd>
            </>
          )}
          {p.tecnico && (
            <>
              <dt>Servidor</dt>
              <dd>{p.tecnico}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}
